import { useState } from 'react';
import { ActionIcon, Badge, Button, Group, Table, Text } from '@mantine/core';
import { IconExternalLink, IconInfoCircle } from '@tabler/icons-react';
import type { NutritionGeneGroup, NutritionVariant } from '@mdev/common';
import { ZygosityBadge } from '../methylation/ZygosityBadge';
import { GeneDrawer } from './GeneDrawer';
import { VariantDetailDialog } from './VariantDetailDialog';
import { GenomeMap } from './GenomeMap';

interface NutritionVariantTableProps {
  geneGroup: NutritionGeneGroup;
  showCNVWarnings?: boolean;
  allVariants?: NutritionVariant[];
}

const EVIDENCE_COLOR: Record<string, string> = {
  Meta: 'green',
  GWAS: 'blue',
  Functional: 'orange',
  Intervention: 'orange',
};

export function NutritionVariantTable({
  geneGroup,
  showCNVWarnings = false,
  allVariants = [],
}: NutritionVariantTableProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<NutritionVariant | null>(null);
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [genomeMapOpen, setGenomeMapOpen] = useState(false);
  const [selectedRsId, setSelectedRsId] = useState<string>('');

  const handleZygosityClick = (variant: NutritionVariant) => {
    setSelectedVariant(variant);
    setVariantDialogOpen(true);
  };

  const handleRsIdClick = (rsId: string) => {
    setSelectedRsId(rsId);
    setGenomeMapOpen(true);
  };

  const rows = geneGroup.variants.map((variant) => {
    const isNonTestable = variant.testable === false;

    return (
      <Table.Tr key={variant.rsid}>
        <Table.Td>
          <Group gap="xs">
            <div>
              <Text fw={500}>{variant.gene}</Text>
              <Text size="sm" c="dimmed">
                {variant.variantName}
              </Text>
            </div>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={() => setDrawerOpen(true)}
            >
              <IconInfoCircle size={16} />
            </ActionIcon>
          </Group>
        </Table.Td>

        <Table.Td>
          <Group gap="xs">
            <Text
              size="sm"
              c="blue"
              style={{ cursor: 'pointer' }}
              onClick={() => handleRsIdClick(variant.rsid)}
            >
              {variant.rsid}
            </Text>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="xs"
              component="a"
              href={`https://www.ncbi.nlm.nih.gov/snp/${variant.rsid}`}
              target="_blank"
            >
              <IconExternalLink size={12} />
            </ActionIcon>
          </Group>
        </Table.Td>

        <Table.Td>
          <Text ff="monospace">{variant.genotype}</Text>
        </Table.Td>

        <Table.Td>
          {isNonTestable ? (
            <Badge color="gray" variant="light" size="sm">
              Not testable
            </Badge>
          ) : (
            <div onClick={() => handleZygosityClick(variant)} style={{ cursor: 'pointer' }}>
              <ZygosityBadge zygosity={variant.zygosity} />
            </div>
          )}
        </Table.Td>

        <Table.Td>
          {variant.evidenceLevel && (
            <Badge
              color={EVIDENCE_COLOR[variant.evidenceLevel] ?? 'gray'}
              variant="light"
              size="sm"
            >
              {variant.evidenceLevel}
            </Badge>
          )}
        </Table.Td>

        <Table.Td>
          {variant.functionalImpact && (
            <Text size="xs" c="dimmed" maw={250}>
              {variant.functionalImpact}
            </Text>
          )}
          {isNonTestable && showCNVWarnings && (
            <Text size="xs" c="yellow" mt={4}>
              ⚠️ CNV not reliably detected on consumer arrays
            </Text>
          )}
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <>
      <div>
        <Group justify="space-between" mb="md">
          <Group gap="md">
            <Text size="lg" fw={600}>
              {geneGroup.gene}
            </Text>
            <Badge
              color={geneGroup.coverage.percentage === 100 ? 'green' : 'yellow'}
              variant="light"
            >
              {geneGroup.coverage.found}/{geneGroup.coverage.total} SNPs (
              {geneGroup.coverage.percentage}%)
            </Badge>
          </Group>

          <Button
            variant="subtle"
            size="sm"
            leftSection={<IconInfoCircle size={16} />}
            onClick={() => setDrawerOpen(true)}
          >
            Gene Info
          </Button>
        </Group>

        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Gene & Variant</Table.Th>
              <Table.Th>rsID</Table.Th>
              <Table.Th>Genotype</Table.Th>
              <Table.Th>Result</Table.Th>
              <Table.Th>Evidence</Table.Th>
              <Table.Th>Functional Impact</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      </div>

      <GeneDrawer
        gene={geneGroup.gene}
        variants={geneGroup.variants}
        references={geneGroup.references || []}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />

      <VariantDetailDialog
        variant={selectedVariant}
        zygDesc={selectedVariant?.zygDesc}
        isOpen={variantDialogOpen}
        onClose={() => setVariantDialogOpen(false)}
      />

      <GenomeMap
        isOpen={genomeMapOpen}
        onClose={() => setGenomeMapOpen(false)}
        allVariants={allVariants}
        selectedRsId={selectedRsId}
      />
    </>
  );
}
